import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import DateFormatter from "../utils/DateFormatter";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Skeleton,
  Box,
  Typography,
} from "@mui/material";

const TableSkeleton = ({ rows = 8 }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Team name</TableCell>
          <TableCell>Trial end</TableCell>          
          <TableCell>Charts</TableCell>
          <TableCell>Members</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton width={180} /></TableCell>
            <TableCell><Skeleton width={120} /></TableCell>            
            <TableCell><Skeleton width={80} /></TableCell>
            <TableCell><Skeleton width={80} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TrialCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [orderBy, setOrderBy] = useState("period_end_time");
  const [order, setOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/api/team/trial-customers`);

        if (Array.isArray(response.data)) {
          setCustomers(response.data);
        } else {
          console.error("Expected an array, but received:", response.data);
          setError("Unexpected response format.");
        }
      } catch (err) {
        console.error("Error fetching customers", err);
        setError("Failed to load customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedCustomers = useMemo(() => {
    const arr = [...customers];
    arr.sort((a, b) => {
      const valA = a?.[orderBy];
      const valB = b?.[orderBy];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return order === "asc" ? valA - valB : valB - valA;
      }

      // Date sort if field looks like a date
      const maybeDateA = new Date(valA);
      const maybeDateB = new Date(valB);
      const isDateA = !isNaN(maybeDateA);
      const isDateB = !isNaN(maybeDateB);
      if (isDateA && isDateB) {
        return order === "asc"
          ? maybeDateA - maybeDateB
          : maybeDateB - maybeDateA;
      }

      const strA = String(valA);
      const strB = String(valB);
      return order === "asc"
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
    return arr;
  }, [customers, orderBy, order]);

  return (
    <div>
      <h2>Trial Customers</h2>

      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <Box sx={{ my: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : customers.length === 0 ? (
        <Box sx={{ my: 2 }}>
          <Typography>No customers found</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Team name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "period_end_time"}
                    direction={orderBy === "period_end_time" ? order : "asc"}
                    onClick={() => handleSort("period_end_time")}
                  >
                    Trial end
                  </TableSortLabel>
                </TableCell>
                
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "usage_percentage"}
                    direction={orderBy === "usage_percentage" ? order : "asc"}
                    onClick={() => handleSort("usage_percentage")}
                  >
                    Charts
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "users_percentage"}
                    direction={orderBy === "users_percentage" ? order : "asc"}
                    onClick={() => handleSort("users_percentage")}
                  >
                    Members
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <a href={`/team/${customer.team_id}`}>{customer.name}</a>
                  </TableCell>
                  <TableCell>
                    {DateFormatter.formatDate(customer.period_end_time)}
                  </TableCell>
                
                  <TableCell>
                    {customer.charts_created != null
                      ? `${customer.charts_created}`
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {customer.team_members != null
                      ? `${customer.team_members}`
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default TrialCustomers;
